// IR-1735 build b (Rotem 65480): which collectors (V1, V2) are set up for an
// avatar and when each last collected. DISPLAY ONLY, and separate from the
// connection status: nothing here disables, confirms or toasts, and the
// Scraper chip and the "Last collected" column are untouched.
//
// Vocabulary rules (Ingestion Matrix, Iron Law 11):
//  - "Enabled" / "Not enabled" come ONLY from a positive report by that
//    collector; everything else reads "Could Not Check" with its reason.
//  - A missing time reads "Not reported" - never "never collected", never 0.
//  - "On two collectors: V1 + V2" is a neutral note (configuration, not a
//    matrix state): no warning colour, no alarm icon.
import type { FleetAvatar } from "@/lib/types";
import { fmtRelative } from "./avatars-tab.helpers";

export type CollectorKey = "v1" | "v2";

export const COLLECTORS_BOTH_NOTE = "On two collectors: V1 + V2";

const STATE_LABEL: Record<string, string> = {
  enabled: "Enabled",
  not_enabled: "Not enabled",
  could_not_check: "Could Not Check",
};

// Short wording is Rotem's (65480: "wording is OK"); the tooltip sentence
// explains it. `report_overwritten` is expected to disappear once the
// collection computer publishes a separate V1 report.
const REASON_TOOLTIP: Record<string, string> = {
  report_missing: "The collection computer has not reported this collector's setup yet.",
  report_overwritten:
    "V1 and V2 write to the same status file, so only the newest is visible. This is temporary.",
  report_stale: "The last report from this collector is older than the allowed age.",
  collector_not_reporting_yet:
    "This collector reports, but does not yet say whether it is enabled.",
  config_unreadable: "The collector reported that it could not read its own configuration.",
};

export function collectorStateLabel(state: string | undefined): string {
  return STATE_LABEL[state ?? ""] ?? "Could Not Check";
}

export function collectorLastText(c: {
  last_collection_at?: string | null;
}): string {
  return c.last_collection_at
    ? `last collected ${fmtRelative(c.last_collection_at)}`
    : "Not reported";
}

function CollectorLine({ k, av, testId }: { k: CollectorKey; av: FleetAvatar; testId: string }) {
  const c = av.collectors?.[k];
  if (!c) {
    return (
      <div data-testid={`${testId}-${k}`} className="iris-td--muted">
        <strong>{k.toUpperCase()}</strong> {"—"}
      </div>
    );
  }
  const cnc = c.state === "could_not_check";
  const reasonText = c.reason_text ?? "no report from this collector";
  return (
    <div data-testid={`${testId}-${k}`} style={{ whiteSpace: "nowrap" }}>
      <strong>{k.toUpperCase()}</strong>{" "}
      <span data-testid={`${testId}-${k}-state`}>{collectorStateLabel(c.state)}</span>
      {cnc && (
        <>
          {" - "}
          <span
            data-testid={`${testId}-${k}-reason`}
            title={REASON_TOOLTIP[c.reason ?? ""] ?? reasonText}
            style={{ cursor: "help", textDecoration: "underline dotted" }}
          >
            {reasonText}
          </span>
        </>
      )}
      <div className="iris-td--muted" style={{ fontSize: 10 }}>
        {collectorLastText(c)}
      </div>
    </div>
  );
}

/** The Collectors cell/block. `testId` differs between the table and the detail. */
export function CollectorsBlock({
  av,
  testId = "avatar-collectors",
}: {
  av: FleetAvatar;
  testId?: string;
}) {
  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {av.collector_summary === "both" && (
        <div
          data-testid="collectors-both-note"
          className="iris-td--muted"
          style={{ fontSize: 11, fontStyle: "italic" }}
        >
          {COLLECTORS_BOTH_NOTE}
        </div>
      )}
      <CollectorLine k="v1" av={av} testId={testId} />
      <CollectorLine k="v2" av={av} testId={testId} />
    </div>
  );
}
